import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '@/api/client';

// ===================================
// Agency Context - Global Agency State
// ===================================

interface Agency {
  id: string;
  name: string;
  slug: string;
  email: string;
  plan: string;
  status: string;
  maxBrands: number;
  maxUsers: number;
  settings?: any;
  createdAt: string;
  updatedAt: string;
  _count?: {
    brands: number;
    users: number;
  };
}

interface AgencyContextType {
  selectedAgency: Agency | null;
  agencies: Agency[];
  isLoading: boolean;
  error: string | null;
  selectAgency: (agencyId: string | null) => void;
  refreshAgencies: () => Promise<void>;
  clearAgencySelection: () => void;
}

const AgencyContext = createContext<AgencyContextType | undefined>(undefined);

interface AgencyProviderProps {
  children: ReactNode;
}

export function AgencyProvider({ children }: AgencyProviderProps) {
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load agencies on mount
  useEffect(() => {
    loadAgencies();
  }, []);

  // Load selected agency from localStorage on mount
  useEffect(() => {
    const savedAgencyId = localStorage.getItem('selectedAgencyId');
    if (savedAgencyId && agencies.length > 0) {
      const agency = agencies.find(a => a.id === savedAgencyId);
      if (agency) {
        setSelectedAgency(agency);
      }
    } else if (agencies.length > 0 && !selectedAgency) {
      // Auto-select first agency if none selected
      selectAgency(agencies[0].id);
    }
  }, [agencies]);

  const loadAgencies = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('/agencies');

      if (response.data.success) {
        setAgencies(response.data.data);
      }
    } catch (err: any) {
      console.error('Error loading agencies:', err);
      setError(err.message || 'Failed to load agencies');
    } finally {
      setIsLoading(false);
    }
  };

  const selectAgency = (agencyId: string | null) => {
    if (!agencyId) {
      clearAgencySelection();
      return;
    }

    const agency = agencies.find(a => a.id === agencyId);
    if (agency) {
      setSelectedAgency(agency);
      localStorage.setItem('selectedAgencyId', agencyId);
    }
  };

  const clearAgencySelection = () => {
    setSelectedAgency(null);
    localStorage.removeItem('selectedAgencyId');
  };

  const refreshAgencies = async () => {
    await loadAgencies();
  };

  const value: AgencyContextType = {
    selectedAgency,
    agencies,
    isLoading,
    error,
    selectAgency,
    refreshAgencies,
    clearAgencySelection
  };

  return (
    <AgencyContext.Provider value={value}>
      {children}
    </AgencyContext.Provider>
  );
}

export function useAgency() {
  const context = useContext(AgencyContext);
  if (context === undefined) {
    throw new Error('useAgency must be used within an AgencyProvider');
  }
  return context;
}

// Export types
export type { Agency, AgencyContextType };
